import Skeleton from "common/components/ui/Skeleton";
import useMetrics from "common/datahooks/useMetrics";
import type { MetricsParameters } from "common/types";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useFlowMetrics from "../../../../datahooks/useFlowMetrics";
import useMetricsList from "../../../../hooks/useMetricsList";

import type { ReactNode } from "react";
import type { CampaignFlow } from "../../../../types";

interface OverviewCardProps {
  flow: CampaignFlow;
  selectedTimeframe: string | MetricsParameters;
}

export default function OverviewCard({
  flow,
  selectedTimeframe,
}: OverviewCardProps) {
  const { id: campaignId } = useCampaignContext();

  const { metricsSum, isLoadingMetrics } = useMetrics({
    selectedTimeframe,
    flowId: flow.id,
    accountIds: [],
  });

  const metricsList = useMetricsList(flow.type);

  const { flowMetrics, isLoadingFlowMetrics } = useFlowMetrics(
    campaignId,
    flow.id,
  );

  return metricsList.map(({ icon: Icon, label, getValue }) => {
    let totalValue: ReactNode;

    if (isLoadingFlowMetrics) {
      totalValue = <Skeleton className="h-4 w-20 ml-auto" />;
    } else if (flowMetrics) {
      totalValue = (
        <p className="ml-auto text-xs text-blue-500">
          Total {getValue(flowMetrics)}
        </p>
      );
    }

    return (
      <div
        key={label}
        className="flex items-center gap-4 border-b border-black-200 py-3"
      >
        <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-2xl bg-black-100">
          <Icon size={20} />
        </span>

        <div className="flex flex-col gap-y-1">
          {isLoadingMetrics ? (
            <>
              <Skeleton className="h-5 w-12" />
              <Skeleton className="h-4 w-24" />
            </>
          ) : (
            <>
              <span className="text-md-bold">{getValue(metricsSum)}</span>
              <h5 className="text-xs-medium text-black-500">{label}</h5>
            </>
          )}
        </div>

        {totalValue}
      </div>
    );
  });
}
